import type {
  CollegeGoal,
  CollegeStrategicPillar,
  FocusArea,
  KeyResult,
  KrStatus,
  Objective,
  ObjectiveStatus,
  Quarter,
  Tactic,
} from "@/types/entities";
import { getFiscalLabel } from "@/lib/fiscal-year";

export interface PrintLookups {
  focusAreas: FocusArea[];
  tactics: Tactic[];
  collegeGoals: CollegeGoal[];
  pillars: CollegeStrategicPillar[];
}

const STATUS_CLASS: Record<ObjectiveStatus | KrStatus, string> = {
  draft: "draft",
  approved: "approved",
  in_progress: "in-progress",
  completed: "completed",
  killed: "killed",
};

const STATUS_FULL: Record<ObjectiveStatus | KrStatus, string> = {
  draft: "Draft",
  approved: "Approved",
  in_progress: "In Progress",
  completed: "Completed",
  killed: "Killed",
};

const STATUS_SHORT: Record<ObjectiveStatus | KrStatus, string> = {
  draft: "Draft",
  approved: "Approved",
  in_progress: "In Prog.",
  completed: "Done",
  killed: "Killed",
};

export function getStatusClass(status: ObjectiveStatus | KrStatus): string {
  return STATUS_CLASS[status] ?? "draft";
}

export function getFullStatusLabel(status: ObjectiveStatus | KrStatus): string {
  return STATUS_FULL[status] ?? status;
}

export function getShortStatusLabel(status: ObjectiveStatus | KrStatus): string {
  return STATUS_SHORT[status] ?? status;
}

const CY_QUARTER_MONTHS: Record<Quarter, string> = {
  Q1: "Jan–Mar",
  Q2: "Apr–Jun",
  Q3: "Jul–Sep",
  Q4: "Oct–Dec",
};

export interface QuarterDisplay {
  calendar: string; // e.g. "Q1 Jan–Mar"
  fiscal: string | null; // e.g. "FY Q3"
}

export function getQuarterDisplay(quarter: Quarter, year: number): QuarterDisplay {
  return {
    calendar: `${quarter} ${CY_QUARTER_MONTHS[quarter]}`,
    fiscal: getFiscalLabel(quarter, year),
  };
}

const NUMBER_WORDS = [
  "zero",
  "one",
  "two",
  "three",
  "four",
  "five",
  "six",
  "seven",
  "eight",
  "nine",
  "ten",
  "eleven",
  "twelve",
];

/**
 * Spell out small counts (0–12) to mirror the template's "of eight" phrasing.
 * Falls back to the numeric form for larger counts.
 */
export function describeCount(n: number): string {
  return NUMBER_WORDS[n] ?? String(n);
}

export function padFolio(n: number): string {
  return String(n).padStart(2, "0");
}

/**
 * The template uses zero-padded Arabic numerals in the .roman span
 * (e.g. "01", "02", "08"). Keep the same shape.
 */
export function objectiveLabelNumber(n: number): string {
  return padFolio(n);
}

interface ResolvedAlignment {
  focusAreas: FocusArea[];
  tactics: Tactic[];
  pillars: CollegeStrategicPillar[];
  goals: CollegeGoal[];
}

/**
 * Resolve the alignment graph for one objective. Pillars/goals are deduped
 * and ordered by the order returned from the API (sort_order on the wire).
 */
export function resolveAlignment(
  objective: Pick<Objective, "focus_area_ids" | "tactic_ids">,
  data: PrintLookups,
): ResolvedAlignment {
  const focusAreas = (objective.focus_area_ids ?? [])
    .map((id) => data.focusAreas.find((fa) => fa.id === id))
    .filter((fa): fa is FocusArea => Boolean(fa));

  const tactics = (objective.tactic_ids ?? [])
    .map((id) => data.tactics.find((t) => t.id === id))
    .filter((t): t is Tactic => Boolean(t));

  const goalIdSet = new Set<string>();
  for (const fa of focusAreas) {
    for (const id of fa.college_goal_ids ?? []) goalIdSet.add(id);
  }
  const goals = data.collegeGoals.filter((g) => goalIdSet.has(g.id));

  const pillarIdSet = new Set<string>();
  for (const g of goals) pillarIdSet.add(g.college_strategic_pillar_id);
  const pillars = data.pillars.filter((p) => pillarIdSet.has(p.id));

  return { focusAreas, tactics, pillars, goals };
}

/**
 * Compact pillar label used in runner headers, num-tag "of N" suffix, and
 * exec-summary obj-list meta. Multiple pillars are joined by " / ".
 * Returns null when the objective has no resolvable pillar context.
 */
export function formatPillarLabel(alignment: ResolvedAlignment): string | null {
  if (alignment.pillars.length === 0) return null;
  return alignment.pillars.map((p) => p.name).join(" / ");
}

/**
 * Full College Alignment cell: pillar names + linked college goal names,
 * joined by " · ". Returns null when no pillars or goals are resolved.
 */
export function formatCollegeAlignment(alignment: ResolvedAlignment): string | null {
  const segments: string[] = [];
  if (alignment.pillars.length > 0) {
    segments.push(alignment.pillars.map((p) => p.name).join(" / "));
  }
  if (alignment.goals.length > 0) {
    for (const g of alignment.goals) segments.push(g.name);
  }
  if (segments.length === 0) return null;
  return segments.join(" · ");
}

/**
 * ITS Focus cell. With one focus area, render "{sort_order} — {name} · {description?}".
 * With multiple, render "{sort_order} — {name}" entries joined by " · ".
 */
export function formatFocusList(alignment: ResolvedAlignment): string | null {
  const fas = alignment.focusAreas;
  if (fas.length === 0) return null;
  if (fas.length === 1) {
    const fa = fas[0]!;
    const head = `${fa.sort_order} — ${fa.name}`;
    const desc = fa.description?.trim();
    return desc ? `${head} · ${desc}` : head;
  }
  return fas.map((fa) => `${fa.sort_order} — ${fa.name}`).join(" · ");
}

/**
 * Strategic Initiative cell. Single tactic: "SI-{ref} — {name}". Multiple:
 * "SI-{ref} (primary), SI-{ref}, … — {primary name}".
 */
export function formatStrategicInitiativeList(alignment: ResolvedAlignment): string | null {
  const tactics = alignment.tactics;
  if (tactics.length === 0) return null;

  const codeOf = (t: Tactic) => (t.reference_code ? `SI-${t.reference_code}` : t.name);

  if (tactics.length === 1) {
    const t = tactics[0]!;
    return t.reference_code ? `SI-${t.reference_code} — ${t.name}` : t.name;
  }

  const primary = tactics[0]!;
  const headParts = tactics.map((t, i) => (i === 0 ? `${codeOf(t)} (primary)` : codeOf(t)));
  return `${headParts.join(", ")} — ${primary.name}`;
}

export interface KrProgressDisplay {
  /** Top-line text inside the kr-progress .frac block. */
  primaryText: string;
  /** Optional fractional pct text (e.g. "0%") rendered in the .pct span. */
  pctText: string | null;
  /** True for binary "Not done" rendering; styles the frac with .notdone. */
  isNotDone: boolean;
  /** 0–100 width for the progress bar, clamped. */
  barWidth: number;
}

export function describeKrProgress(kr: KeyResult): KrProgressDisplay {
  const pct = Math.round(kr.completion_percent);
  const barWidth = Math.max(0, Math.min(100, pct));
  if (kr.type === "binary") {
    const done = kr.completion_percent >= 100;
    return {
      primaryText: done ? "Done" : "Not done",
      pctText: `${pct}%`,
      isNotDone: !done,
      barWidth,
    };
  }
  if (kr.type === "metric" && kr.target_value != null) {
    const cur = kr.current_value ?? 0;
    return {
      primaryText: `${cur} / ${kr.target_value}`,
      pctText: `${pct}%`,
      isNotDone: false,
      barWidth,
    };
  }
  return {
    primaryText: `${pct}%`,
    pctText: null,
    isNotDone: false,
    barWidth,
  };
}

/**
 * Trim an objective title to a runner-footer "short title".
 * Keeps the first ~5 words then appends an ellipsis when the title is longer.
 */
export function shortTitle(title: string, maxWords = 5): string {
  const words = title.trim().split(/\s+/);
  if (words.length <= maxWords) return title.trim();
  return `${words.slice(0, maxWords).join(" ")}…`;
}

export interface ExecSummaryStats {
  objectiveCount: number;
  krCount: number;
  inProgressCount: number;
  draftCount: number;
  approvedCount: number;
  completedCount: number;
  killedCount: number;
  cumulativePct: number | null;
}

export function computeExecStats(objectives: Objective[]): ExecSummaryStats {
  let krCount = 0;
  let inProgress = 0;
  let draft = 0;
  let approved = 0;
  let completed = 0;
  let killed = 0;
  let pctSum = 0;
  let pctCount = 0;
  for (const o of objectives) {
    krCount += o.kr_total ?? 0;
    if (o.status === "in_progress") inProgress += 1;
    else if (o.status === "draft") draft += 1;
    else if (o.status === "approved") approved += 1;
    else if (o.status === "completed") completed += 1;
    else if (o.status === "killed") killed += 1;
    if (typeof o.completion_percent === "number") {
      pctSum += o.completion_percent;
      pctCount += 1;
    }
  }
  return {
    objectiveCount: objectives.length,
    krCount,
    inProgressCount: inProgress,
    draftCount: draft,
    approvedCount: approved,
    completedCount: completed,
    killedCount: killed,
    cumulativePct: pctCount === 0 ? null : Math.round(pctSum / pctCount),
  };
}

